<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WgptIntegrationsAPI from 'dashboard/api/wgpt/integrations';
import WgptSallaOrderItem from './WgptSallaOrderItem.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const contact = useFunctionGetter('contacts/getContact', props.contactId);

const hasSearchableInfo = computed(
  () => !!contact.value?.email || !!contact.value?.phone_number
);

const orders = ref([]);
const loading = ref(true);
const error = ref('');

const fetchOrders = async () => {
  try {
    loading.value = true;
    error.value = '';
    orders.value = [];
    const response = await WgptIntegrationsAPI.getSallaOrders(props.contactId);
    orders.value = response.data.orders;
  } catch (e) {
    const errorCode = e.response?.data?.error_code;
    if (errorCode) {
      const key = `CONVERSATION_SIDEBAR.WGPT_SALLA.ERRORS.${errorCode.toUpperCase()}`;
      error.value = t(key) === key ? e.response.data.error_code : t(key);
    } else {
      error.value =
        e.response?.data?.error ||
        t('CONVERSATION_SIDEBAR.WGPT_SALLA.ERRORS.DEFAULT');
    }
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.contactId,
  () => {
    if (hasSearchableInfo.value) {
      fetchOrders();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="!hasSearchableInfo" class="text-center text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.WGPT_SALLA.NO_SALLA_ORDERS') }}
    </div>
    <div v-else-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-center text-n-ruby-12">
      {{ error }}
    </div>
    <div v-else-if="!orders.length" class="text-center text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.WGPT_SALLA.NO_SALLA_ORDERS') }}
    </div>
    <div v-else>
      <WgptSallaOrderItem
        v-for="order in orders"
        :key="order.id"
        :order="order"
      />
    </div>
  </div>
</template>
